import { useMutation, useQuery } from '@apollo/client';
import {
  AcceptOffersIcon,
  AlertService,
  Button,
  ColorBadge,
  Column,
  DataTableNaked,
  RowAction,
  RowActionInstanceUnaward,
  Search,
  Timing,
  usePolling,
} from '@spoiler-alert/ui-library';
import type { History } from 'history';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import deleteCachedFieldsOnListedInventoriesQuery from '../../apollo/cache-helpers/delete-cached-fields-on-listed-inventories-query';
import deleteCachedFieldsOnUserQuery from '../../apollo/cache-helpers/delete-cached-fields-on-user-query';
import UpdatedAwardedOffers from '../../components/banners/updated-awarded-offers';
import type { DataTableNakedType } from '../../components/component-types';
import { getColumnsFromDataTableProfile } from '../../components/data-table';
import PostAwardUpdatesModal from '../../components/modals/post-award-updates-modal';
import featureFlags from '../../enums/feature-flags';
import { acceptOffer, SendLossEmails, UnawardOfferListingsByBuyerSiteId } from '../../graphql/mutations';
import { AwardSummaryQuery, OfferReviewPollingQuery, ProgressBarsQuery } from '../../graphql/queries';
import type { AwardSummary, AwardSummaryLine, User } from '../../graphql/query-result-types';
import checkFeature from '../../helpers/check-feature-flag';
import routePaths from '../../route-paths';
import { postAwardUpdatesFilters } from '../offer-comparison/offer-comparison-filter-operations';
import { TitleService } from '../../services';
import { Breadcrumbs, UserFilters } from '../../store';
import { OfferComparisonStrings } from '../../string-resources';
import styles from './offers-styles';
import SendLossEmailsModal from './send-loss-emails-modal';

const useStyles = createUseStyles(styles);

const EXPORT_STATUS = {
  NOT_IN_PROGRESS: 'NOT_IN_PROGRESS',
  STARTED: 'STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETE: 'COMPLETE',
};

const exportStatusStringMap: Record<string, string> = {
  IN_PROGRESS: 'ACCEPTANCE IN PROGRESS',
  COMPLETE: 'ACCEPTED',
  NOT_IN_PROGRESS: 'NOT ACCEPTED',
  STARTED: 'PENDING',
};

const badgeTheme: Record<string, React.ComponentProps<typeof ColorBadge>['theme']> = {
  'ACCEPTANCE IN PROGRESS': 'yellow',
  ACCEPTED: 'green',
  'NOT ACCEPTED': 'grey',
  PENDING: 'yellow',
};

const UI_LOCK_STATUS = {
  OPEN: 'OPEN',
  LOCKED: 'LOCKED',
};

const SUMMED_FIELDS = [
  'acceptedOffer',
  'casesAwarded',
  'cubeAdjustedWeightAwarded',
  'donatedCost',
  'netObsolescenceImpact',
  'originalQuantity',
  'palletsAwarded',
  'priceOfInventoryAwarded',
  'poundsAwarded',
  'totalCases',
  'totalCost',
  'totalPrice',
  'totalWeight',
  'writeOffPrice',
  'writeOffPriceOfInventoryAwarded',
  'unitPriceRecoveryRate',
  'totalPostAwardUpdates',
] as const;

interface OfferReviewPolling {
  uiLockStatus: string;
  displayPostAwardUpdates: number;
  exportStatuses: { buyerSiteName: string; exportStatus: string }[];
  timestamp: string;
}

interface ErrorResponse {
  code: string;
  message: string;
}

type OfferReviewRow = Omit<AwardSummaryLine, 'costRecoveryRate' | 'discountPercent' | 'writeOffDiscountPercent'> & {
  costRecoveryRate: number | null;
  discountPercent: number | null;
  writeOffDiscountPercent: number | null;
};

interface OfferReviewProps {
  user: User;
  history: History;
}

const TypedDataTableNaked = DataTableNaked as DataTableNakedType<OfferReviewRow>;

const OfferReview: React.FC<OfferReviewProps> = ({ user, history }) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');
  const [exportsStarted, setExportsStarted] = useState(false);
  const [acceptOfferMutation, { loading: accepting }] = useMutation<{ acceptOffer: { errors: string[] } }>(acceptOffer);
  const [acceptRowMutation, { loading: acceptingRow }] = useMutation<{ acceptOffer: { errors: string[] } }>(acceptOffer);
  const [unawardOfferListingsByBuyerSiteId] = useMutation<{ unawardOfferListingsByBuyerSiteId: { errors: ErrorResponse[] } }>(
    UnawardOfferListingsByBuyerSiteId,
  );
  const [exportInProgress, setExportInProgress] = useState(false);
  const [showSendLossEmailsModal, setShowSendLossEmailsModal] = useState(false);
  const [displayAwardedOffersModal, setDisplayAwardedOffersModal] = useState(false);
  const [acceptingRowIds, setAcceptingRowIds] = useState<string[]>([]);
  const [unawardingRowIds, setUnawardingRowIds] = useState<string[]>([]);
  const postAwardModalRef = useRef(false);
  const lastPostAwardUpdatesRef = useRef<number | undefined>(undefined);
  const awardSummaryResults = useQuery<AwardSummary>(AwardSummaryQuery);
  const [sendLossEmails] = useMutation<{ sendLossEmails: { errors: ErrorResponse[] } }>(SendLossEmails);

  const statusPolling: OfferReviewPolling | undefined = usePolling({
    query: OfferReviewPollingQuery,
    parameters: undefined,
    poll: true,
    responseKey: 'offerReviewPollingQuery',
    idleInterval: 10000,
    activeInterval: 1000,
    isInProgress: () => accepting || acceptingRow,
    skip: undefined,
    fetchPolicy: undefined,
    pauseWhenHidden: true,
    onPollComplete: (status: OfferReviewPolling) => {
      if (exportsStarted && !accepting && status.uiLockStatus === UI_LOCK_STATUS.OPEN) awardSummaryResults.refetch();
    },
  });

  const { refetch: refetchAwardSummary } = awardSummaryResults;
  const awardSummaryEmpty = !awardSummaryResults.data?.currentUserQuery.getAwardSummary.results.length;
  const uiLockStatus = statusPolling?.uiLockStatus;
  const displayPostAwardUpdates = statusPolling?.displayPostAwardUpdates;

  const statusBadgeByBuyerMap = useMemo(() => {
    const statusMap = new Map<string, string>();
    statusPolling?.exportStatuses.forEach((e) => {
      const statusBadgeKey = exportStatusStringMap[e.exportStatus];
      statusMap.set(e.buyerSiteName, statusBadgeKey);
    });
    return statusMap;
  }, [statusPolling]);

  const buyerList = useMemo(() => {
    if (!awardSummaryResults.data) return [];
    const buyerMap = new Map<string, AwardSummaryLine>();
    awardSummaryResults.data.currentUserQuery.getAwardSummary.results.forEach((result) => {
      const buyer = buyerMap.get(result.siteId);
      if (!buyer) {
        buyerMap.set(result.siteId, { ...result, _id: result.siteId });
        return;
      }
      SUMMED_FIELDS.forEach((field) => {
        buyer[field] += result[field];
      });
      if (result.exportStatus !== EXPORT_STATUS.NOT_IN_PROGRESS) buyer.exportStatus = result.exportStatus;
    });
    return [...buyerMap.values()].map(
      (buyer): OfferReviewRow => ({
        ...buyer,
        costRecoveryRate: buyer.totalCost ? (buyer.acceptedOffer / buyer.totalCost) * 100 : null,
        discountPercent: buyer.totalPrice ? (1 - buyer.acceptedOffer / buyer.totalPrice) * 100 : null,
        writeOffDiscountPercent: buyer.writeOffPriceOfInventoryAwarded
          ? (1 - buyer.acceptedOffer / buyer.writeOffPriceOfInventoryAwarded) * 100
          : null,
      }),
    );
  }, [awardSummaryResults.data]);

  useEffect(() => {
    let inProgress = false;
    const awardSummaryTimestamp = awardSummaryResults.data?.currentUserQuery.getAwardSummary.timestamp;
    if (awardSummaryTimestamp && statusPolling && awardSummaryTimestamp > statusPolling.timestamp) {
      awardSummaryResults.data?.currentUserQuery.getAwardSummary.results?.forEach((result) => {
        if (exportStatusStringMap[result.exportStatus] === exportStatusStringMap.IN_PROGRESS) inProgress = true;
      });
    } else {
      statusPolling?.exportStatuses.forEach((e) => {
        if (exportStatusStringMap[e.exportStatus] === exportStatusStringMap.IN_PROGRESS) inProgress = true;
      });
    }
    setExportInProgress(inProgress);
  }, [awardSummaryResults.data, statusPolling]);

  useEffect(() => {
    TitleService.setTitles('Offer Review');
    Breadcrumbs.set([
      {
        url: routePaths.review,
        title: 'Offer Review',
      },
    ]);
  }, []);

  useEffect(() => {
    if (!displayAwardedOffersModal && postAwardModalRef.current) {
      postAwardModalRef.current = false;
      UserFilters.updateFilters('offerComparison', postAwardUpdatesFilters);
      history.push(routePaths.offerComparison);
    }
  }, [displayAwardedOffersModal, history]);

  useEffect(() => {
    if (exportsStarted && awardSummaryEmpty && uiLockStatus === UI_LOCK_STATUS.OPEN) {
      AlertService.alert({
        type: 'success',
        message: <span>All offers have successfully been accepted</span>,
        autoDismiss: true,
        dismissDelay: 3000,
      });
      setExportsStarted(false);
      refetchAwardSummary();
    }
  }, [exportsStarted, awardSummaryEmpty, uiLockStatus, refetchAwardSummary]);

  useEffect(() => {
    if (lastPostAwardUpdatesRef.current !== undefined && lastPostAwardUpdatesRef.current !== displayPostAwardUpdates) refetchAwardSummary();
    lastPostAwardUpdatesRef.current = displayPostAwardUpdates;
  }, [displayPostAwardUpdates, refetchAwardSummary]);

  const noOffersToAccept = () =>
    (!statusPolling || statusPolling?.uiLockStatus === UI_LOCK_STATUS.OPEN) &&
    !awardSummaryResults.data?.currentUserQuery.getAwardSummary.results?.filter((result) => {
      return exportStatusStringMap[result.exportStatus] === exportStatusStringMap.NOT_IN_PROGRESS;
    }).length;

  const acceptAllDisabled =
    !statusPolling || statusPolling.uiLockStatus === UI_LOCK_STATUS.LOCKED || accepting || acceptingRow || exportInProgress || noOffersToAccept();

  const rowStatus = (row: OfferReviewRow) => {
    const status = statusBadgeByBuyerMap.get(row.siteName);
    if (status === exportStatusStringMap[EXPORT_STATUS.NOT_IN_PROGRESS] && row.exportStatus !== EXPORT_STATUS.NOT_IN_PROGRESS) {
      return exportStatusStringMap[row.exportStatus];
    }
    return status || exportStatusStringMap[EXPORT_STATUS.NOT_IN_PROGRESS];
  };

  const profileColumns: Column[] = getColumnsFromDataTableProfile('Offer Comp Buyer Flyup and Offer Review', user.site.dataTableProfiles);
  const columns = [
    ...profileColumns
      .filter((col) => !['transactionLocationName', 'logisticsTerm'].includes(col.field))
      .map(
        (col) =>
          new Column({
            field: col.field,
            displayName: col.displayName,
            sortable: col.sortable,
            visible: col.visible,
            defaultSortDirection: col.defaultSortDirection,
            formatter: (value: unknown, row: OfferReviewRow) => (value === null ? '-' : col.render(value, row)),
            ...(col.field === 'siteName' ? { visible: true, defaultSort: true, defaultSortDirection: 'asc' } : {}),
          }),
      ),
    new Column({
      field: 'exportStatus',
      displayName: 'Status',
      formatter: (_value: string, row: OfferReviewRow) => {
        const status = rowStatus(row);
        return (
          <ColorBadge id={`offer-review-status-${row.siteId}`} theme={badgeTheme[status]}>
            {status}
          </ColorBadge>
        );
      },
    }),
  ];

  const numberOfPostAwardUpdatedOffers = buyerList.reduce((sum, buyer) => sum + buyer.totalPostAwardUpdates, 0);

  const displayPostUpdatesAward = !!displayPostAwardUpdates;

  const rowActionsDisabled = statusPolling?.uiLockStatus === UI_LOCK_STATUS.LOCKED;
  const disabledRowIds = [
    ...acceptingRowIds,
    ...unawardingRowIds,
    ...buyerList.filter((row) => rowStatus(row) !== exportStatusStringMap[EXPORT_STATUS.NOT_IN_PROGRESS]).map((row) => row.siteId),
  ];

  const error = () => {
    AlertService.alert({
      type: 'warning',
      message: (
        <span>
          {'Sorry there was an error accepting all offers. If this problem persists, please contact a Spoiler Alert Administrator to help you.'}
        </span>
      ),
      autoDismiss: false,
      dismissDelay: undefined,
    });
  };

  const handleDynamicRowClick = (row: OfferReviewRow) => {
    const status = statusBadgeByBuyerMap.get(row.siteName);
    if (status !== exportStatusStringMap[EXPORT_STATUS.NOT_IN_PROGRESS] || row.exportStatus !== EXPORT_STATUS.NOT_IN_PROGRESS) return;
    history.push(`${routePaths.offerReview}/${row.siteId}/${encodeURIComponent(row.siteName)}`);
  };

  const handleSearch = Timing.debounce((text: string) => setSearchText(text), 300);

  const displayAwardedOfferModalHandler = () => {
    setDisplayAwardedOffersModal(true);
  };

  const navigateToOfferCompScreen = () => {
    postAwardModalRef.current = true;
    setDisplayAwardedOffersModal(false);
  };

  const closeAwardedOffersModal = () => {
    setDisplayAwardedOffersModal(false);
  };

  const runRowAction = async (row: OfferReviewRow, setRowIds: React.Dispatch<React.SetStateAction<string[]>>, action: () => Promise<void>) => {
    setRowIds((ids) => [...ids, row.siteId]);
    try {
      await action();
    } finally {
      setRowIds((ids) => ids.filter((id) => id !== row.siteId));
    }
  };

  const handleAcceptRowClick = (row: OfferReviewRow) => {
    if (row.totalPostAwardUpdates > 0) return displayAwardedOfferModalHandler();
    return runRowAction(row, setAcceptingRowIds, async () => {
      const response = await acceptRowMutation({
        variables: { buyerSiteIds: [row.siteId], acceptAll: false },
        update: (cache) => {
          deleteCachedFieldsOnListedInventoriesQuery(cache);
        },
        refetchQueries: [{ query: ProgressBarsQuery }, { query: AwardSummaryQuery }],
      });
      if (response?.data?.acceptOffer?.errors?.length) {
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{OfferComparisonStrings.awardingError}</span>,
          dismissDelay: undefined,
        });
        return;
      }
      AlertService.alert({
        type: 'info',
        autoDismiss: true,
        message: <span>{`Acceptance for ${row.siteName} has been initiated.`}</span>,
        dismissDelay: undefined,
      });
    });
  };

  const handleUnawardRowClick = (row: OfferReviewRow) =>
    runRowAction(row, setUnawardingRowIds, async () => {
      const response = await unawardOfferListingsByBuyerSiteId({
        variables: { siteId: row.siteId },
        update: (cache) => {
          deleteCachedFieldsOnUserQuery(cache, ['awardedOfferListings', 'awardedInventoryFilterParameters', 'getAwardSummary']);
        },
        refetchQueries: [{ query: ProgressBarsQuery }],
      });
      if (response?.data?.unawardOfferListingsByBuyerSiteId?.errors?.length) {
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{OfferComparisonStrings.unawardingError}</span>,
          dismissDelay: undefined,
        });
        return;
      }
      AlertService.alert({
        type: 'success',
        autoDismiss: true,
        message: <span>{`All offers from ${row.siteName} have been unawarded.`}</span>,
        dismissDelay: undefined,
      });
    });

  const handleAcceptAll = Timing.debounce(async () => {
    setExportsStarted(true);
    const buyerSiteIds = buyerList.map((b) => b.siteId);
    const { data } = await acceptOfferMutation({
      variables: { buyerSiteIds, acceptAll: true },
      update: (cache) => {
        deleteCachedFieldsOnUserQuery(cache, ['awardedOfferListings', 'awardedInventoryFilterParameters', 'getAwardSummary']);
        deleteCachedFieldsOnListedInventoriesQuery(cache);
      },
    });

    if (data?.acceptOffer.errors.length) {
      error();
      setExportsStarted(false);
      awardSummaryResults.refetch();
    }
  }, 1000);

  const onSendLossEmailsSubmit = async (storefrontName: string, dropDate: string) => {
    const { data } = await sendLossEmails({
      variables: { storefrontName, dropDate },
    });

    if (data?.sendLossEmails.errors.length) {
      AlertService.alert({
        type: 'warning',
        message: (
          <span>
            {'Sorry there was an error sending loss emails. If this problem persists, please contact a Spoiler Alert Administrator to help you.'}
          </span>
        ),
        autoDismiss: false,
        dismissDelay: undefined,
      });
    } else {
      AlertService.alert({
        type: 'success',
        message: <span>{'Loss emails sent successfully'}</span>,
        autoDismiss: false,
        dismissDelay: undefined,
      });
    }
  };

  return (
    <div id="offers">
      <div className={classes.actionBar}>
        <Search onChange={handleSearch} className={classes.offerReviewSearch} />
        {checkFeature(featureFlags.sendLossEmails) && (
          <Button secondary onClick={() => setShowSendLossEmailsModal(true)}>
            Send Loss Emails
          </Button>
        )}
        <Button
          disabled={acceptAllDisabled}
          loading={accepting}
          onClick={!displayPostUpdatesAward ? handleAcceptAll : displayAwardedOfferModalHandler}
          icon={AcceptOffersIcon}
          loadingText="Accepting All Offers"
          cypressTag="accept-all-offers-button"
        >
          Accept All Offers
        </Button>
      </div>
      {displayPostUpdatesAward && <UpdatedAwardedOffers onClick={displayAwardedOfferModalHandler} />}
      <div className={classes.table_wrap}>
        <TypedDataTableNaked
          data={buyerList}
          loading={awardSummaryResults.loading}
          columns={columns}
          searchText={searchText}
          onRowClick={handleDynamicRowClick}
          rowActions={[
            <RowActionInstanceUnaward
              key="unaward"
              onClick={(row: OfferReviewRow) => () => handleUnawardRowClick(row)}
              disabled={rowActionsDisabled}
              disabledRows={disabledRowIds}
              loadingRows={unawardingRowIds}
            />,
            <RowAction
              key="accept"
              tooltipText="Accept Offers"
              loadingTooltipText="Accepting Offers"
              icon={AcceptOffersIcon}
              onClick={(row: OfferReviewRow) => () => handleAcceptRowClick(row)}
              disabled={rowActionsDisabled}
              disabledRows={disabledRowIds}
              loadingRows={acceptingRowIds}
            />,
          ]}
          sticky
          cypressTagTable="offer-review-table"
        />
      </div>
      <SendLossEmailsModal
        onHide={() => setShowSendLossEmailsModal(false)}
        open={showSendLossEmailsModal}
        onSubmit={onSendLossEmailsSubmit}
        user={user}
      />
      <PostAwardUpdatesModal
        displayAwardedOffersModal={displayAwardedOffersModal}
        navigateToOfferCompScreen={navigateToOfferCompScreen}
        cancel={closeAwardedOffersModal}
        modalInnerText={`${numberOfPostAwardUpdatedOffers} of your customer's have offers that have changed since they were awarded. Offers cannot be accepted for these customers until the updated offers are approved or rejected.`}
      />
    </div>
  );
};

export default OfferReview;
